import React, { useState, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { db } from "services/firebase";
import firebase from "firebase/compat/app";
import { calculateHlthdskScore } from "utils/locationProcessing";
import {
  DIAGNOSTIC_CAPABILITY_OPTIONS,
  DIAGNOSTIC_CAPABILITY_UI_GROUPS,
  CAPABILITY_VERIFICATION_STATUS,
} from "constants/diagnosticCapabilities";
import {
  deriveLegacyCapabilitiesFromV11,
  normalizeDiagnosticCapabilities,
  resolveAttestedKeysForForm,
} from "constants/diagnosticCapabilityMigration";
import { isEmergencyDepartment } from "constants/facilityTypes";

import {
  Alert,
  Box,
  FormControlLabel,
  Typography,
  FormGroup,
  Checkbox,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

const FORM_MAX_WIDTH = 540;

const LABEL_BY_KEY = Object.fromEntries(
  DIAGNOSTIC_CAPABILITY_OPTIONS.map((o) => [o.key, o.label]),
);

const CapabilitiesForm = ({ data }) => {
  const { handleSubmit, control, reset } = useForm({
    defaultValues: { caps: {} },
  });
  const [loadingCapabilities, setLoadingCapabilities] = useState(false);
  const [savedSummary, setSavedSummary] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const groups = useMemo(() => DIAGNOSTIC_CAPABILITY_UI_GROUPS, []);

  useEffect(() => {
    if (!data?.id) return;
    const attested = resolveAttestedKeysForForm(data);
    const caps = {};
    for (const opt of DIAGNOSTIC_CAPABILITY_OPTIONS) {
      caps[opt.key] = attested.includes(opt.key);
    }
    reset({ caps });
  }, [data, reset]);

  const onSubmit = (values) => {
    if (!data?.id) return;
    setLoadingCapabilities(true);
    setSaveError(null);
    setSavedSummary(null);

    const selected = Object.entries(values.caps || {})
      .filter(([, on]) => on === true)
      .map(([key]) => key);
    const diagnosticCapabilities = normalizeDiagnosticCapabilities(selected);
    const capabilities = deriveLegacyCapabilitiesFromV11(diagnosticCapabilities);

    const updatedData = {
      ...data,
      capabilities,
      diagnosticCapabilities,
    };
    const hlthdskScore = calculateHlthdskScore(updatedData);

    const updateData = {
      hlthdsk_score: hlthdskScore,
      capabilities,
      diagnosticCapabilities,
    };

    // Self-attest so clinics become rank-eligible (UC/ED already eligible).
    if (!isEmergencyDepartment(data)) {
      updateData.capabilityVerification = {
        status: CAPABILITY_VERIFICATION_STATUS.VERIFIED,
        attestedAt: firebase.firestore.FieldValue.serverTimestamp(),
        source: "status_board_self_attest",
      };
    }

    db.collection("locations")
      .doc(String(data.id))
      .update(updateData)
      .then(() => {
        setLoadingCapabilities(false);
        setSaveError(null);
        setSavedSummary("Capabilities have been updated successfully.");
      })
      .catch(() => {
        setLoadingCapabilities(false);
        setSavedSummary(null);
        setSaveError("Error submitting the form. Please try again later.");
      });
  };

  return (
    <>
      {savedSummary != null && (
        <Alert
          severity="success"
          sx={{ mb: 2, maxWidth: FORM_MAX_WIDTH }}
          onClose={() => setSavedSummary(null)}
        >
          {savedSummary}
        </Alert>
      )}
      {saveError && (
        <Alert
          severity="error"
          sx={{ mb: 2, maxWidth: FORM_MAX_WIDTH }}
          onClose={() => setSaveError(null)}
        >
          {saveError}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ maxWidth: FORM_MAX_WIDTH, mb: 3 }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            What on-site diagnostic capabilities do you have at your facility?
            These are used to match patients from CareStart to facilities that
            can perform the tests they are likely to need.
          </Typography>

          {groups.map((group) => (
            <Box key={group.id} sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {group.title}
              </Typography>
              <FormGroup>
                {group.keys.map((key) => (
                  <Controller
                    key={key}
                    name={`caps.${key}`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(value)}
                            onChange={(e) => onChange(e.target.checked)}
                            inputProps={{
                              "data-testid": `capability-toggle-${key}`,
                            }}
                          />
                        }
                        label={LABEL_BY_KEY[key] || key}
                      />
                    )}
                  />
                ))}
              </FormGroup>
            </Box>
          ))}

          <LoadingButton
            disabled={loadingCapabilities || !data?.id}
            loading={loadingCapabilities}
            type="submit"
            variant="contained"
            size="large"
            data-testid="capabilities-update-submit"
          >
            Update Capabilities
          </LoadingButton>
        </Box>
      </form>
    </>
  );
};

export default CapabilitiesForm;
